import Big from 'big.js';
import _ from 'lodash';
import {
  ONLY_ZEROS,
  toNonDivisibleNumber,
  toReadableNumber,
} from '../utils/numbers';
import { getLiquidity } from '../utils/pool';
import getConfigV2 from './configV2';
import { FEATURES } from './config';
import { TokenMetadata } from './ft-contract';
import { isStableToken } from './near';
import {
  getPoolsByTokens,
  getPoolsByTokensAurora,
  getRefPoolsByToken1ORToken2,
  Pool,
} from './pool';

import { cacheAllDCLPools, checkCacheDCLPools } from './swapV3';
import {
  createSmartRouteLogicWorker,
  transformWorkerResult,
} from './smartRouteLogicWorker';
import {
  IEstimateSwapServerView,
  estimateSwapFromServer,
  getUsedPools,
  getUsedTokens,
} from './smartRouterFromServer';
import { getTokenPriceListFromCacheForServer } from '../services/smartRouterFromServer';
import { EstimateSwapOptions, EstimateSwapView } from './swapTypes';
import { quoteClmmPair } from './clmmPool';
import { WRAP_NEAR_CONTRACT_ID } from './wrap-near';
import {
  getOneSwapActionResult,
  getHybridStableSmart,
  getOneSwapActionResultAurora,
} from './swapRouteEstimates';
const { NO_REQUIRED_REGISTRATION_TOKEN_IDS } = getConfigV2();

const smartRouteLogicWorker = createSmartRouteLogicWorker();

// Server-side routing only runs against your own router (REACT_APP_SMART_ROUTER_URL);
// otherwise routes are computed in the browser from your exchange's pools.
const SHUTDOWN_SERVER = !FEATURES.smartRouter;
export const estimateSwap = async ({
  tokenIn,
  tokenOut,
  amountIn,
  intl,
  setLoadingData,
  loadingTrigger,
  supportLedger,
  proGetCachePool,
  slippage,
}: EstimateSwapOptions): Promise<{
  estimates?: EstimateSwapView[];
  estimatesFromServer?: IEstimateSwapServerView;
  tag: string;
  source: 'script' | 'server';
  poolsMap?: Record<string, Pool>;
  tokensMap?: Record<string, TokenMetadata>;
}> => {
  if (!SHUTDOWN_SERVER) {
    const resultFromServer = await estimateSwapFromServer({
      tokenIn,
      tokenOut,
      amountIn: toNonDivisibleNumber(tokenIn.decimals, amountIn),
      slippage,
      supportLedger,
    }).catch(() => ({}));
    if (
      !(
        resultFromServer?.result_code !== 0 ||
        !resultFromServer?.result_data?.routes?.length
      )
    ) {
      const routes = resultFromServer.result_data?.routes;
      let poolsMap = {};
      let tokensMap = {};
      try {
        const isValidCached = checkCacheDCLPools();
        if (!isValidCached) {
          await cacheAllDCLPools();
        }
      } catch (error) {}
      try {
        const prices = await getTokenPriceListFromCacheForServer();
        const isLosePrice =
          !prices?.[tokenIn.id]?.price || !prices?.[tokenOut.id]?.price;
        poolsMap = await getUsedPools(routes, isLosePrice);
      } catch (error) {}
      try {
        tokensMap = await getUsedTokens(routes);
      } catch (error) {}
      return {
        estimatesFromServer: resultFromServer.result_data,
        tag: `${tokenIn.id}-${toNonDivisibleNumber(
          tokenIn.decimals,
          amountIn
        )}-${tokenOut.id}`,
        source: 'server',
        poolsMap,
        tokensMap,
      };
    } else {
      return estimateScriptOrClmm({
        tokenIn,
        tokenOut,
        amountIn,
        intl,
        setLoadingData,
        loadingTrigger,
        supportLedger,
        proGetCachePool,
      });
    }
  } else {
    return estimateScriptOrClmm({
      tokenIn,
      tokenOut,
      amountIn,
      intl,
      setLoadingData,
      loadingTrigger,
      supportLedger,
      proGetCachePool,
    });
  }
};

function fungibleId(token: TokenMetadata) {
  if (
    token.id === 'near' ||
    token.id === WRAP_NEAR_CONTRACT_ID ||
    token.symbol === 'NEAR'
  ) {
    return WRAP_NEAR_CONTRACT_ID;
  }
  return token.id;
}

async function estimateScriptOrClmm(options: EstimateSwapOptions): Promise<{
  estimates: EstimateSwapView[];
  tag: string;
  source: 'script';
}> {
  const clmm = await estimateClmmSwap(options).catch(() => null);
  if (clmm) return clmm;
  const resultFromScript = await estimateSwapFromScript(options);
  return { ...resultFromScript, source: 'script' };
}

async function estimateClmmSwap(options: EstimateSwapOptions): Promise<{
  estimates: EstimateSwapView[];
  tag: string;
  source: 'script';
} | null> {
  const amountInRaw = toNonDivisibleNumber(
    options.tokenIn.decimals,
    options.amountIn
  );
  if (ONLY_ZEROS.test(amountInRaw)) return null;
  const quoted = await quoteClmmPair({
    tokenInId: fungibleId(options.tokenIn),
    tokenOutId: fungibleId(options.tokenOut),
    amountInRaw,
  });
  if (!quoted) return null;
  const estimate = toReadableNumber(
    options.tokenOut.decimals,
    quoted.amountOutRaw
  );
  const tag = `${options.tokenIn.id}-${amountInRaw}-${options.tokenOut.id}`;
  return {
    source: 'script',
    tag,
    estimates: [
      {
        estimate,
        inputToken: options.tokenIn.id,
        outputToken: options.tokenOut.id,
        partialAmountIn: amountInRaw,
        totalInputAmount: amountInRaw,
        tokens: [options.tokenIn, options.tokenOut],
        pool: {
          id: 0,
          tokenIds: [quoted.pool.currency0, quoted.pool.currency1],
          supplies: {},
          fee: Math.round(Number(quoted.pool.fee) / 100),
          shareSupply: '0',
          tvl: 0,
          token0_ref_price: '0',
          partialAmountIn: amountInRaw,
          Dex: 'clmm',
        },
        clmm: quoted,
      } as EstimateSwapView,
    ],
  };
}
const estimateSwapFromScript = async ({
  tokenIn,
  tokenOut,
  amountIn,
  intl,
  setLoadingData,
  loadingTrigger,
  supportLedger,
  proGetCachePool,
}: EstimateSwapOptions): Promise<{
  estimates: EstimateSwapView[];
  tag: string;
}> => {
  const parsedAmountIn = toNonDivisibleNumber(tokenIn.decimals, amountIn);

  sessionStorage.setItem('loadingTrigger', loadingTrigger.toString());

  const tag = `${tokenIn.id}-${parsedAmountIn}-${tokenOut.id}`;

  if (ONLY_ZEROS.test(parsedAmountIn))
    throw new Error(
      `${amountIn} ${intl.formatMessage({ id: 'is_not_a_valid_swap_amount' })}`
    );

  const throwNoPoolError = () => {
    throw new Error(
      `${intl.formatMessage({
        id: 'no_pool_available_to_make_a_swap_from',
      })} ${tokenIn?.symbol} -> ${tokenOut?.symbol} ${intl.formatMessage({
        id: 'for_the_amount',
      })} ${amountIn} ${intl.formatMessage({
        id: 'no_pool_eng_for_chinese',
      })}`
    );
  };

  let { filteredPools: pools, pool_protocol } = await getPoolsByTokens({
    tokenInId: tokenIn.id,
    tokenOutId: tokenOut.id,
    amountIn: parsedAmountIn,
    setLoadingData,
    loadingTrigger,
    tokenIn,
    tokenOut,
    proGetCachePool,
  });

  pools = pools.filter((pool: any) => {
    return getLiquidity(pool, tokenIn, tokenOut) > 0;
  });

  const { supportLedgerRes } = await getOneSwapActionResult(
    pools,
    loadingTrigger,
    tokenIn,
    tokenOut,
    pool_protocol === 'rpc',
    throwNoPoolError,
    amountIn,
    parsedAmountIn
  );

  if (supportLedger || pool_protocol === 'rpc') {
    return { estimates: supportLedgerRes, tag };
  }

  const orpools = await getRefPoolsByToken1ORToken2();

  let stableSmartEstimates;
  let smartRouteV2OutputEstimate;

  try {
    const stableSmartActionsV2 = transformWorkerResult(
      await smartRouteLogicWorker.getStableSmart({
        pools: orpools.filter((pool) => !pool?.Dex || pool.Dex !== 'tri'),
        inputToken: tokenIn.id,
        outputToken: tokenOut.id,
        totalInput: parsedAmountIn,
      })
    );
    //const stableSmartActionsV2 = await stableSmart(
    // orpools.filter((p) => !p?.Dex || p.Dex !== 'tri'),
    // tokenIn.id,
    // tokenOut.id,
    // parsedAmountIn
    // );

    stableSmartEstimates = stableSmartActionsV2;

    smartRouteV2OutputEstimate = stableSmartActionsV2
      .filter((action) => action.outputToken == action.routeOutputToken)
      .map((action) => new Big(action.estimate))
      .reduce((total, estimate) => total.plus(estimate), new Big(0))
      .toString();
  } catch (error) {
    console.error('smartRouteV2OutputEstimate error', error);
  }

  let bestEstimate = smartRouteV2OutputEstimate || 0;
  // console.log('bestEstimate', bestEstimate);
  // hybrid smart routing
  if (isStableToken(tokenIn.id) || isStableToken(tokenOut.id)) {
    const hybridStableSmart = await getHybridStableSmart(
      tokenIn,
      tokenOut,
      amountIn,
      loadingTrigger
    );

    const hybridStableSmartOutputEstimate =
      hybridStableSmart.estimate.toString();

    if (
      new Big(
        hybridStableSmartOutputEstimate === 'NaN'
          ? '0'
          : hybridStableSmartOutputEstimate
      ).gt(bestEstimate)
    ) {
      bestEstimate = hybridStableSmartOutputEstimate || 0;

      stableSmartEstimates = hybridStableSmart.actions;
    }
  }

  if (
    new Big(supportLedgerRes?.[0]?.estimate || 0).gt(new Big(bestEstimate || 0))
  ) {
    stableSmartEstimates = supportLedgerRes;
  }
  if (!stableSmartEstimates?.length) {
    throwNoPoolError();
  }

  return { estimates: stableSmartEstimates, tag };
};

export const estimateSwapAurora = async ({
  tokenIn,
  tokenOut,
  amountIn,
  intl,
  setLoadingData,
  loadingTrigger,
  swapPro,
  proGetCachePool,
}: EstimateSwapOptions): Promise<{
  estimates: EstimateSwapView[];
}> => {
  const parsedAmountIn = toNonDivisibleNumber(tokenIn.decimals, amountIn);

  if (ONLY_ZEROS.test(parsedAmountIn))
    throw new Error(
      `${amountIn} ${intl.formatMessage({ id: 'is_not_a_valid_swap_amount' })}`
    );

  const throwNoPoolError = () => {
    throw new Error(
      `${intl.formatMessage({
        id: 'no_pool_available_to_make_a_swap_from',
      })} ${tokenIn?.symbol} -> ${tokenOut?.symbol} ${intl.formatMessage({
        id: 'for_the_amount',
      })} ${amountIn} ${intl.formatMessage({
        id: 'no_pool_eng_for_chinese',
      })}`
    );
  };

  const pools = (
    await getPoolsByTokensAurora({
      tokenInId: tokenIn.id,
      tokenOutId: tokenOut.id,
      amountIn: parsedAmountIn,
      setLoadingData,
      loadingTrigger,
      crossSwap: swapPro,
      tokenIn,
      tokenOut,
      proGetCachePool,
    })
  ).filter((pool) => {
    return getLiquidity(pool, tokenIn, tokenOut) > 0;
  });

  const { triTodos } = await getOneSwapActionResultAurora(
    pools,
    tokenIn,
    tokenOut,
    throwNoPoolError,
    parsedAmountIn
  );

  return { estimates: triTodos };
};
